import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  EmptyState,
  InfiniteScrollSentinel,
  ListSearch,
  ListSummary,
} from '@/components/ui/cifras/list-toolbar';
import {
  InitialsAvatar,
  ResourceCard,
  ResourceCardGrid,
} from '@/components/ui/cifras/resource-card';
import { Content } from '@/components/ui/content';
import { ActionsButton } from '@/components/ui/data-table';
import { useRouter } from '@/hooks/use-router';
import { meta } from '@/lib/meta';
import { artistKeys, useDeleteArtist, useInfiniteArtists } from '@/services/artists';
import { usePermissions } from '@/services/auth';

export const Route = createFileRoute('/_app/artists/')({
  head: () => meta({ label: 'Artistas' }),
  component: RouteComponent,
});

const model = 'artists';
const PER_PAGE = 24;

function RouteComponent() {
  const { push } = useRouter();
  const { createPermissions } = usePermissions();
  const { canCreate, canUpdate, canDelete } = createPermissions(model);
  const { mutateAsync: remove } = useDeleteArtist();

  const [search, setSearch] = useState('');

  const query = {
    search: search || undefined,
    orderBy: 'name',
    order: 'asc',
    perPage: PER_PAGE,
  } as const;
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteArtists(query);

  const handleSearch = (term: string) => {
    setSearch(term);
  };

  const artists = data?.pages.flatMap((page) => page.data) ?? [];
  const total = data?.pages[0]?.pagination.total ?? 0;

  return (
    <Content
      breadcrumbs={{
        icon: 'mic-vocal',
        title: 'Artistas',
        action: canCreate && (
          <Button variant="success" onClick={() => push('/artists/create')} icon="plus">
            Adicionar
          </Button>
        ),
        items: [{ label: 'Listagem', active: true }],
      }}
    >
      <div className="flex flex-col gap-5">
        <ListSearch placeholder="Buscar artista..." onSearch={handleSearch} />

        {data && <ListSummary shown={artists.length} total={total} noun="artistas" />}

        {data && artists.length === 0 ? (
          <EmptyState
            title="Nenhum artista encontrado"
            hint={search ? 'Tente outro termo de busca.' : undefined}
          />
        ) : (
          <ResourceCardGrid min={280}>
            {artists.map((artist) => (
              <ResourceCard
                key={artist.id}
                muted={artist.isActive === false}
                leading={<InitialsAvatar name={artist.name} />}
                title={artist.name}
                subtitle={
                  <>
                    {artist.songCount} {artist.songCount === 1 ? 'cifra' : 'cifras'}
                    {artist.keys.length > 0 && ` · tons ${artist.keys.join(', ')}`}
                  </>
                }
                trailing={
                  artist.isActive === false ? (
                    <Badge variant="destructive">Inativo</Badge>
                  ) : undefined
                }
                actions={
                  <ActionsButton
                    id={artist.id}
                    edit={canUpdate}
                    remove={canDelete}
                    onEdit={(id) => push(`/artists/${id}`)}
                    onRemove={(id) => remove({ id })}
                    queryKey={artistKeys.all}
                  />
                }
                onClick={canUpdate ? () => push(`/artists/${artist.id}`) : undefined}
              />
            ))}
          </ResourceCardGrid>
        )}

        <InfiniteScrollSentinel
          hasNextPage={Boolean(hasNextPage)}
          isFetching={isFetchingNextPage}
          onLoadMore={fetchNextPage}
        />
      </div>
    </Content>
  );
}
